import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, Check, CheckCheck, History, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const MUTE_KEY = 'leaveDecisionChimeMuted';
let audioCtx = null;
const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  return audioCtx;
};
// Unlock audio on the first user interaction (browsers block autoplay).
if (typeof window !== 'undefined') {
  const unlock = () => { getCtx()?.resume?.(); window.removeEventListener('pointerdown', unlock); };
  window.addEventListener('pointerdown', unlock);
}

// Soft two-note chime (E5 → A5) built with the Web Audio API.
const playChime = () => {
  const ctx = getCtx();
  if (!ctx) return;
  ctx.resume?.();
  [[659.25, 0], [880, 0.18]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const t = ctx.currentTime + delay;
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.12, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.95);
  });
};

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const AlertRow = ({ a, onConfirm, confirmed }) => {
  const approved = a.status === 'approved';
  const applied = a.kind === 'applied';
  const tone = applied ? ['border-sky-200', 'bg-sky-100 text-sky-700', 'New application'] : approved ? ['border-emerald-200', 'bg-emerald-100 text-emerald-700', 'Approved'] : ['border-red-200', 'bg-red-100 text-red-700', 'Rejected'];
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 bg-white ${tone[0]} ${confirmed ? 'opacity-70' : ''}`} data-testid={`leave-decision-alert-${a.id}`}>
      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${tone[1]}`}>{tone[2]}</span>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-semibold text-gray-900">{a.employee_name} <span className="text-xs font-normal text-gray-500">{a.employee_code} · {a.department}</span></p>
        <p className="text-xs text-gray-600">
          <span className="capitalize">{(a.leave_type || '').replace(/_/g, ' ')}</span> · {fmtDate(a.start_date)} – {fmtDate(a.end_date)} · {a.days} day{a.days === 1 ? '' : 's'}
          {' · '}{applied ? <>applied via {a.source} · awaiting manager approval</> : <>{approved ? 'approved' : 'rejected'} by <b>{a.decided_by}</b> via {a.source}</>} · {fmtTime(a.decided_at)}
          {a.note ? <span className="italic"> · “{a.note}”</span> : null}
        </p>
      </div>
      {confirmed ? (
        <span className="text-[11px] text-gray-400">Seen {fmtTime(a.confirmed_at)}</span>
      ) : (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onConfirm(a.id)} data-testid={`confirm-leave-alert-${a.id}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Seen
        </Button>
      )}
    </div>
  );
};

export default function LeaveDecisionAlertsCard() {
  const [data, setData] = useState({ new: [], recent_confirmed: [] });
  const [showRecent, setShowRecent] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === '1');
  const seenIds = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const load = useCallback(() => {
    api.get('/api/dashboard/leave-decision-alerts').then((r) => {
      const ids = r.data.new.map((a) => a.id);
      if (seenIds.current && !mutedRef.current && ids.some((id) => !seenIds.current.has(id))) playChime();
      seenIds.current = new Set([...(seenIds.current || []), ...ids]);
      setData(r.data);
    }).catch(() => {});
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem(MUTE_KEY, next ? '1' : '0');
    if (!next) playChime();
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const confirm = async (id) => {
    try {
      await api.post(`/api/dashboard/leave-decision-alerts/${id}/confirm`);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not confirm', variant: 'destructive' });
    }
  };
  const confirmAll = async () => {
    try {
      const { data: r } = await api.post('/api/dashboard/leave-decision-alerts/confirm-all');
      toast({ title: "Marked as seen", description: `${r.confirmed} notification(s) cleared.` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not confirm', variant: 'destructive' });
    }
  };

  const hasNew = data.new.length > 0;
  if (!hasNew) return null; // hidden until a new application / decision arrives

  return (
    <Card className={`border-2 ${hasNew ? 'border-amber-400 leave-alert-blink' : 'border-gray-200'}`} data-testid="leave-decision-alerts-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <BellRing className={`h-5 w-5 ${hasNew ? 'text-amber-600' : 'text-gray-400'}`} />
          <h3 className="font-semibold text-gray-900">Leave notifications</h3>
          {hasNew && (
            <span className="relative inline-flex items-center text-[11px] font-bold text-white bg-red-600 rounded-full px-2 py-0.5" data-testid="leave-decision-new-badge">
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60" />
              <span className="relative">NEW · {data.new.length}</span>
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <button type="button" onClick={toggleMute} title={muted ? 'Chime off — click to turn on' : 'Chime on — click to mute'}
              className="p-1 rounded hover:bg-gray-100 text-gray-500" data-testid="leave-decision-chime-toggle">
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4 text-amber-600" />}
            </button>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <History className="h-3.5 w-3.5" /> Recently seen (7 days)
              <Switch checked={showRecent} onCheckedChange={setShowRecent} data-testid="leave-decision-show-recent" />
            </label>
            {hasNew && (
              <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={confirmAll} data-testid="confirm-all-leave-alerts">
                <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all seen
              </Button>
            )}
          </div>
        </div>
        {hasNew ? data.new.map((a) => <AlertRow key={a.id} a={a} onConfirm={confirm} />) : (
          <p className="text-xs text-gray-500">No new leave applications or manager decisions — you're all caught up.</p>
        )}
        {showRecent && (
          <div className="space-y-2 pt-2 border-t" data-testid="leave-decision-recent-list">
            {data.recent_confirmed.length ? data.recent_confirmed.map((a) => <AlertRow key={a.id} a={a} confirmed />) : (
              <p className="text-xs text-gray-500">Nothing marked seen in the last 7 days.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
